"use client";

import React, { useState } from "react";
import { useDeviceAndWalletDetection } from "@/hooks/useDeviceAndWalletDetection";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";
import {
	MYGEOTOKENS_WALLET_DISPLAY_NAME,
	getWalletDisplayName,
} from "@/constants/wallet-branding";
import Image from "next/image";
import { ChevronDown, Wallet, Smartphone, Check } from "lucide-react";

interface WalletSwitcherProps {
	onWalletChange?: (wallet: any) => void;
	className?: string;
}

/**
 * Account switching component for desktop users
 * Allows switching between different linked wallets (embedded + Phantom)
 */
export function WalletSwitcher({
	onWalletChange,
	className = "",
}: WalletSwitcherProps) {
	const {
		connect,
		connectedWallets,
		activeWallet,
		switchWallet,
		canSwitchWallets,
		isSignedIn,
	} = usePrivyWallet();
	const { isMobile, hasPhantomExtension } = useDeviceAndWalletDetection();

	const [isOpen, setIsOpen] = useState(false);

	// Don't show on mobile or if no multiple wallets
	if (isMobile || !canSwitchWallets || !isSignedIn) {
		return null;
	}

	const handleWalletSwitch = async (wallet: any) => {
		try {
			// Get wallet address
			const address = wallet.address || (wallet as any).walletAddress;
			if (!address) {
				console.error(" Wallet address not found");
				return;
			}

			// Use the enhanced switchWallet method
			const success = await switchWallet(address);

			if (success) {
				setIsOpen(false);
				onWalletChange?.(wallet);
			} else {
				console.error(" Failed to switch wallet");
			}
		} catch (error) {
			console.error(" Wallet switch error:", error);
		}
	};

	const getWalletIcon = (walletType: string) => {
		switch (walletType) {
			case "phantom":
				return (
					<div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
						<Image
							src="/phantom-icon.svg"
							alt={MYGEOTOKENS_WALLET_DISPLAY_NAME}
							width={16}
							height={16}
							className="phantom-icon"
						/>
					</div>
				);
			case "privy":
				return (
					<div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
						<Smartphone className="w-4 h-4 text-white" />
					</div>
				);
			default:
				return (
					<div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center">
						<Wallet className="w-4 h-4 text-white" />
					</div>
				);
		}
	};

	const getWalletName = (walletType: string) => getWalletDisplayName(walletType);

	const formatAddress = (address: string) => {
		return `${address.slice(0, 6)}...${address.slice(-4)}`;
	};

	return (
		<div className={`relative ${className}`}>
			{/* Current wallet display */}
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="flex items-center gap-3 bg-[#1A1A1A] border border-gray-700 rounded-lg px-4 py-3 hover:border-gray-600 transition-colors"
			>
				{activeWallet &&
					getWalletIcon(activeWallet.walletClientType || "privy")}
				<div className="flex-1 text-left">
					<p className="text-white text-sm font-medium">
						{activeWallet
							? getWalletName(activeWallet.walletClientType || "privy")
							: "Select Wallet"}
					</p>
					{activeWallet && (
						<p className="text-gray-400 text-xs">
							{formatAddress(
								activeWallet.address || (activeWallet as any).walletAddress
							)}
						</p>
					)}
				</div>
				<ChevronDown
					className={`w-4 h-4 text-gray-400 transition-transform ${
						isOpen ? "rotate-180" : ""
					}`}
				/>
			</button>

			{/* Dropdown menu */}
			{isOpen && (
				<div className="absolute top-full left-0 right-0 mt-2 bg-[#1A1A1A] border border-gray-700 rounded-lg shadow-xl z-50">
					<div className="p-2">
						{connectedWallets.map((wallet, index) => {
							if (!wallet) return null;

							const walletAddress =
								wallet.address || (wallet as any).walletAddress;
							const activeWalletAddress =
								activeWallet?.address || (activeWallet as any)?.walletAddress;
							const isActive = walletAddress === activeWalletAddress;

							return (
								<button
									key={index}
									onClick={() => handleWalletSwitch(wallet)}
									className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors ${
										isActive
											? "bg-green-500/20 border border-green-500/30"
											: "hover:bg-gray-800"
									}`}
								>
									{getWalletIcon(wallet.walletClientType || "privy")}
									<div className="flex-1 text-left">
										<p className="text-white text-sm font-medium">
											{getWalletName(wallet.walletClientType || "privy")}
										</p>
										<p className="text-gray-400 text-xs">
											{formatAddress(walletAddress)}
										</p>
									</div>
									{isActive && <Check className="w-4 h-4 text-green-500" />}
								</button>
							);
						})}
					</div>

					{/* Add wallet option */}
					{hasPhantomExtension &&
						!connectedWallets.find(
							(w) => w && w.walletClientType === "phantom"
						) && (
							<div className="border-t border-gray-700 p-2">
								<button
									onClick={() => {
										setIsOpen(false);
										// Trigger the Phantom linking flow using the connect method
										connect("phantom").catch(console.error);
									}}
									className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-gray-800 transition-colors"
								>
									<div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
										<Image
											src="/phantom-icon.svg"
											alt={MYGEOTOKENS_WALLET_DISPLAY_NAME}
											width={16}
											height={16}
											className="phantom-icon"
										/>
									</div>
									<div className="flex-1 text-left">
										<p className="text-white text-sm font-medium">
											Link {MYGEOTOKENS_WALLET_DISPLAY_NAME}
										</p>
										<p className="text-gray-400 text-xs">
											Connect your browser wallet extension
										</p>
									</div>
								</button>
							</div>
						)}
				</div>
			)}

			{/* Backdrop */}
			{isOpen && (
				<div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
			)}
		</div>
	);
}
